import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OField, inputCls, NavButtons } from "@/components/onboard/ui";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Info } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CarrierSetup() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [ch, setCh] = useState(null);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const start = async () => {
    setLoading(true);
    try { const { data } = await api.post("/auth/carrier-setup/start", { email, phone }); setCh(data); toast.success("Verification code sent to your phone"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const complete = async () => {
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try { const { data } = await api.post("/auth/carrier-setup/complete", { challenge_id: ch.challenge_id, code, password: pw }); setUser(data); toast.success("Welcome to Asterism"); navigate("/carrier-portal"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#05070d] text-white grid place-items-center px-5 relative overflow-hidden" data-testid="carrier-setup-page">
      <div className="pointer-events-none absolute -top-40 left-1/4 w-[500px] h-[500px] rounded-full bg-sky-500/20 blur-[140px]" />
      <div className="relative w-full max-w-md rounded-[2rem] border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-8">
        <div className="flex items-center gap-2.5 mb-8"><img src="/freightx-logo.webp" alt="" className="w-8 h-8" /><span className="font-heading font-extrabold">Asterism Carrier App</span></div>
        <h1 className="font-heading font-extrabold text-3xl">{ch ? "Create Your Password" : "First-Time Login"}</h1>
        <p className="text-white/55 mt-2 text-sm">{ch ? "Enter the code sent to your phone and choose a password." : "Use the same email and phone number you used during onboarding."}</p>
        {!ch ? (
          <div className="space-y-4 mt-7">
            <OField label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="cs-email" /></OField>
            <OField label="Phone Number"><input value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} data-testid="cs-phone" /></OField>
            <NavButtons onNext={start} loading={loading} disabled={!email || !phone} nextLabel="Send Code" testid="cs-start" />
          </div>
        ) : (
          <div className="space-y-4 mt-7">
            <InputOTP maxLength={6} value={code} onChange={(v) => setCode(v.replace(/\D/g, ""))} data-testid="cs-code">
              <InputOTPGroup className="gap-2">{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg font-mono font-bold rounded-xl border border-white/15 bg-white/5 text-white" />)}</InputOTPGroup>
            </InputOTP>
            {ch.dev_code && <p className="text-xs text-amber-200 bg-amber-400/10 border border-amber-400/20 rounded-xl px-3 py-2 flex items-center gap-2" data-testid="cs-dev-code"><Info size={14} />Test mode: SMS not connected. Code: <b className="font-mono tracking-widest">{ch.dev_code}</b></p>}
            <OField label="New Password"><input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} data-testid="cs-password" /></OField>
            <OField label="Confirm Password"><input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={inputCls} data-testid="cs-password2" /></OField>
            <NavButtons onNext={complete} loading={loading} disabled={code.length !== 6 || !pw} nextLabel="Create Password & Sign In" testid="cs-complete" />
          </div>
        )}
        <p className="mt-6 text-center text-sm text-white/45">Already set up? <Link to="/login" className="text-sky-300">Sign in</Link></p>
      </div>
    </div>
  );
}
